/* MUI */
import { Box, TableCell, Typography } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Route } from 'application/Shared/Helpers';

/*  Objects  */
import { Application, ApplicationBusinessOwner } from 'objects/Application';
import {
    Contract,
    ContractAlertingIconWithText,
    ContractDate,
    ContractTCV,
    ContractType
} from 'objects/Contract';

/* React */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Internal Components */
import { ContractSectionTitle } from './ContractSectionTitle';

const getHeaders = ({ byContractType, t }) => {
    if (byContractType) {
        return [
            {
                align: 'left',
                field: 'name',
                renderHeader: <strong>{t('Common.application')}</strong>,
                sortable: true,
                sortableFieldType: 'string',
                sortableKey: 'application.name',
                tooltip: 'application',
                width: '20%'
            },
            {
                align: 'left',
                field: 'business_owner',
                renderHeader: <strong>{t('Common.business_owner')}</strong>,
                tooltip: 'business_owner',
                width: '20%'
            },
            {
                align: 'left',
                field: 'alerting',
                renderHeader: <strong>{t('Common.alerting')}</strong>,
                width: '20%'
            },
            {
                align: 'left',
                field: 'renewalDate',
                renderHeader: <strong>{t('Common.contract_date')}</strong>,
                tooltip: 'renewalDate',
                width: '20%'
            },
            {
                align: 'left',
                field: 'tcv',
                renderHeader: (
                    <strong>{t('Reports.Contracts.totalContractValue')}</strong>
                ),
                width: '20%'
            }
        ];
    }
    return [
        {
            align: 'left',
            field: 'name',
            renderHeader: <strong>{t('Common.application')}</strong>,
            sortable: true,
            sortableFieldType: 'string',
            sortableKey: 'application.name',
            tooltip: 'application',
            width: '20%'
        },
        {
            align: 'left',
            field: 'business_owner',
            renderHeader: <strong>{t('Common.business_owner')}</strong>,
            tooltip: 'business_owner',
            width: '20%'
        },
        {
            align: 'left',
            field: 'alerting',
            renderHeader: <strong>{t('Common.alerting')}</strong>,
            width: '10%'
        },
        {
            align: 'left',
            field: 'type',
            renderHeader: <strong>{t('Common.type')}</strong>,
            tooltip: 'contract_management_monthly_renewal_date',
            width: '20%'
        },
        {
            align: 'left',
            field: 'renewalDate',
            renderHeader: <strong>{t('Common.contract_date')}</strong>,
            tooltip: 'renewalDate',
            width: '15%'
        },
        {
            align: 'left',
            field: 'tcv',
            renderHeader: <strong>{t('Common.tcv')}</strong>,
            tooltip: 'contract_management_tcv',
            width: '15%'
        }
    ];
};

const Subtitle = ({ subtitle }) => (
    <Typography variant="h6" fontWeight={600} mt="16px">
        {subtitle}
    </Typography>
);

export const ContractsLists = ({ contracts }) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();

    const route = Route.buildBaseAdminRoute({ clientId, partnerId });

    const monthlyContracts = useMemo(
        () => contracts.filter((contract) => contract.type === 'month'),
        [contracts]
    );

    const renewalContracts = useMemo(
        () => contracts.filter((contract) => contract.type === 'renew'),
        [contracts]
    );

    const headersByContractType = useMemo(
        () => getHeaders({ byContractType: true, t }),
        [t]
    );
    const headersNotByContractType = useMemo(
        () => getHeaders({ byContractType: false, t }),
        [t]
    );

    return (
        <Box data-testid="contractsLists" mb="24px">
            <ContractSectionTitle title={t('Reports.Contracts.tableByType')} />
            <Box mt="24px">
                <>
                    <Subtitle subtitle={t('Reports.Contracts.monthToMonth')} />
                    <Box mt="24px" mb="32px">
                        {!(monthlyContracts.length > 0) ? (
                            <Typography variant="body2">
                                {t('Reports.Contracts.noEventMatch')}
                            </Typography>
                        ) : (
                            <ManagedTable
                                headers={headersByContractType}
                                renderBody={(row) => (
                                    <Application
                                        data={row.application}
                                        nameLinkTo={`${route}/application/${row.application.id}/contract`}
                                    >
                                        <TableCell>
                                            <ApplicationNameLinkCell
                                                withCategory
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <ApplicationBusinessOwner />
                                        </TableCell>
                                        <Contract data={row}>
                                            <TableCell>
                                                <ContractAlertingIconWithText />
                                            </TableCell>
                                            <TableCell>
                                                <ContractDate
                                                    abbreviatedMonthDate
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <ContractTCV />
                                            </TableCell>
                                        </Contract>
                                    </Application>
                                )}
                                renderBodyKey="id"
                                rows={monthlyContracts}
                                rowsPerPage={-1}
                                sortDirection="ASC"
                                sortFieldType="string"
                                sortKey="application.name"
                            />
                        )}
                    </Box>
                    <Subtitle
                        subtitle={t('Reports.Contracts.renewalContracts')}
                    />
                    <Box mt="24px">
                        {!(renewalContracts.length > 0) ? (
                            <Typography variant="body2">
                                {t('Reports.Contracts.noEventMatch')}
                            </Typography>
                        ) : (
                            <ManagedTable
                                headers={headersByContractType}
                                renderBody={(row) => (
                                    <Application
                                        data={row.application}
                                        nameLinkTo={`${route}/application/${row.application.id}/contract`}
                                    >
                                        <TableCell>
                                            <ApplicationNameLinkCell
                                                withCategory
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <ApplicationBusinessOwner />
                                        </TableCell>
                                        <Contract data={row}>
                                            <TableCell>
                                                <ContractAlertingIconWithText />
                                            </TableCell>
                                            <TableCell>
                                                <ContractDate
                                                    abbreviatedMonthDate
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <ContractTCV />
                                            </TableCell>
                                        </Contract>
                                    </Application>
                                )}
                                renderBodyKey="id"
                                rows={renewalContracts}
                                rowsPerPage={-1}
                                sortDirection="ASC"
                                sortFieldType="string"
                                sortKey="application.name"
                            />
                        )}
                    </Box>
                </>
            </Box>

            <Box>
                <ContractSectionTitle title={t('Reports.Contracts.all')} />
                <Box mt="32px">
                    <ManagedTable
                        headers={headersNotByContractType}
                        renderBody={(row) => (
                            <Application
                                data={row.application}
                                nameLinkTo={`${route}/application/${row.application.id}/contract`}
                            >
                                <TableCell>
                                    <ApplicationNameLinkCell withCategory />
                                </TableCell>
                                <TableCell>
                                    <ApplicationBusinessOwner />
                                </TableCell>
                                <Contract data={row}>
                                    <TableCell>
                                        <ContractAlertingIconWithText />
                                    </TableCell>
                                    <TableCell>
                                        <ContractType />
                                    </TableCell>
                                    <TableCell>
                                        <ContractDate abbreviatedMonthDate />
                                    </TableCell>
                                    <TableCell>
                                        <ContractTCV />
                                    </TableCell>
                                </Contract>
                            </Application>
                        )}
                        renderBodyKey="id"
                        rows={contracts}
                        rowsPerPage={-1}
                        sortDirection="ASC"
                        sortFieldType="string"
                        sortKey="application.name"
                    />
                </Box>
            </Box>
        </Box>
    );
};
